import React from 'react';
import clsx from 'clsx';

import makeStyles from '@material-ui/core/styles/makeStyles';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Button from '@material-ui/core/Button';

import { roleScopes } from '../Organisation/roleScopes';

const useStyles = makeStyles((theme) => ({
	table: {
		width: '100%',
		overflowX: 'hidden',
		[theme.breakpoints.up('md')]: {
			overflowX: 'unset',
		},
	},
	headerCell: {
		border: 'none',
		textAlign: 'right',
		borderRight: '1px solid ' + theme.palette.greyFour.main,
		color: theme.palette.greyTwo.main,
		[theme.breakpoints.down('md')]: {
			paddingLeft: 0,
			paddingRight: theme.spacing(2),
		},
	},
	noBottomBorder: {
		border: 'none',
		[theme.breakpoints.up('sm')]: {
			width: '55%',
		},
		[theme.breakpoints.down('md')]: {
			paddingLeft: theme.spacing(1),
			paddingRight: 0,
		},
	},
	roleButtonGroup: {
		'&:not(:last-child)': {
			borderRight: '1px solid #ccc',
		},
	},
	textRoleButton: {
		padding: '6px 16px',
	},
}));

// 0 = None, 1 = Read, 2 = Write. read and write are independent scopes; the
// level is the highest present.
const levelFor = (scopes, realm) => {
	if (!Array.isArray(scopes)) return 0;
	if (scopes.includes(`${realm}:write`)) return 2;
	if (scopes.includes(`${realm}:read`)) return 1;
	return 0;
};

/**
 * None/Read/Write picker for a personal access token's scopes, mirroring the
 * organisation role editor. Offers the full realm list from `roleScopes` (the
 * same set the roles page exposes — including `application` and `partner`); the
 * auth server re-checks the requested scopes against the caller's effective
 * permissions on create/update.
 *
 * @param value    the token's currently selected scopes
 * @param onChange called with the next scopes array
 */
export const TokenScopeSelector = ({ value, onChange }) => {
	const classes = useStyles();

	const setLevel = (realm, level) => {
		const scopes = (value ?? []).filter(
			(s) => s !== `${realm}:read` && s !== `${realm}:write`
		);
		if (level >= 1) scopes.push(`${realm}:read`);
		if (level >= 2) scopes.push(`${realm}:write`);
		onChange(scopes);
	};

	return (
		<TableContainer className={classes.table}>
			<Table size='small'>
				<TableBody>
					{roleScopes.map((realm) => {
						const level = levelFor(value, realm);
						return (
							<TableRow key={realm}>
								<TableCell className={classes.headerCell}>{realm}</TableCell>
								<TableCell className={classes.noBottomBorder}>
									<ButtonGroup disableElevation>
										<Button
											color='default'
											variant={level === 0 ? 'contained' : 'text'}
											onClick={() => setLevel(realm, 0)}
											className={clsx(
												classes.textRoleButton,
												classes.roleButtonGroup
											)}>
											None
										</Button>
										<Button
											color={level === 1 ? 'secondary' : 'default'}
											variant={level === 1 ? 'contained' : 'text'}
											onClick={() => setLevel(realm, 1)}
											className={clsx(
												classes.textRoleButton,
												classes.roleButtonGroup
											)}>
											Read
										</Button>
										<Button
											color={level === 2 ? 'primary' : 'default'}
											variant={level === 2 ? 'contained' : 'text'}
											onClick={() => setLevel(realm, 2)}
											className={clsx(
												classes.textRoleButton,
												classes.roleButtonGroup
											)}>
											Write
										</Button>
									</ButtonGroup>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
